'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import { motion, useAnimation, useDragControls } from 'motion/react';
import { cn } from '@src/v3/lib/utils';

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  /** Header content (e.g. title + close button). Rendered at top of sheet. */
  headerContent?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Sheet height as ratio of viewport (0–1). Default 0.6 (same as CustomBottomSheet at 0.6 snap). */
  sheetHeightRatio?: number;
  /** Ref to the element that should act as the drag handle. Only dragging from this element will close the sheet. */
  dragHandleRef?: React.RefObject<HTMLElement | null>;
  /** Threshold in pixels to drag down before closing. Default 100. */
  dragCloseThreshold?: number;
}

/**
 * Bottom sheet for pickers (e.g. country code).
 * Slides up from bottom, closes on overlay click or header close button.
 * Optionally supports drag-to-close from a specific drag handle element.
 * Uses CSS dvh (dynamic viewport height) for smooth iOS Safari behavior.
 * Content area has no overflow so the child (e.g. scrollable list) is the scroll container.
 */
const BottomSheet: React.FC<BottomSheetProps> = ({
  open,
  onClose,
  headerContent,
  children,
  className,
  sheetHeightRatio = 0.6,
  dragHandleRef,
  dragCloseThreshold = 100,
}) => {
  const controls = useAnimation();
  const overlayControls = useAnimation();
  const dragControls = useDragControls();
  const hasAnimatedIn = useRef(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  // Animate sheet and overlay in when opened (only once per open)
  useEffect(() => {
    if (open && !hasAnimatedIn.current) {
      hasAnimatedIn.current = true;
      controls.start({ y: 0 });
      overlayControls.start({ opacity: 1 });
    }
    if (!open) {
      hasAnimatedIn.current = false;
    }
  }, [open, controls, overlayControls]);

  // Body scroll lock when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [open]);

  // Handle drag start from the drag handle element
  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      if (dragHandleRef?.current) {
        dragControls.start(e);
      }
    },
    [dragControls, dragHandleRef]
  );

  // Attach pointer down listener to drag handle
  useEffect(() => {
    const handle = dragHandleRef?.current;
    if (open && handle) {
      handle.addEventListener('pointerdown', handlePointerDown);
      return () => {
        handle.removeEventListener('pointerdown', handlePointerDown);
      };
    }
    return undefined;
  }, [open, dragHandleRef, handlePointerDown]);

  // Handle drag end - close if dragged down past threshold
  const handleDragEnd = useCallback(
    (
      event: MouseEvent | TouchEvent | PointerEvent,
      info: { offset: { y: number }; velocity: { y: number } }
    ) => {
      event.stopPropagation();

      const { offset, velocity } = info;
      // Close if dragged down past threshold or with high velocity
      if (offset.y > dragCloseThreshold || velocity.y > 500) {
        // Animate both sheet and overlay out simultaneously with fast tween, then call onClose
        const closeDuration = 0.2;
        Promise.all([
          controls.start(
            { y: '100%' },
            { type: 'tween', duration: closeDuration, ease: 'easeOut' }
          ),
          overlayControls.start({ opacity: 0 }, { duration: closeDuration }),
        ]).then(() => onClose());
      } else {
        // Snap back to open position
        controls.start({ y: 0 });
      }
    },
    [controls, overlayControls, dragCloseThreshold, onClose]
  );

  if (!open) return null;

  // Convert ratio to percentage for CSS (e.g., 0.6 -> 60)
  const heightPercent = sheetHeightRatio * 100;

  // Enable drag only if dragHandleRef is provided
  const isDragEnabled = !!dragHandleRef;

  return (
    <>
      {/* Overlay – tap to close */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={overlayControls}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-[rgba(0,0,0,0.5)] z-[59]"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="presentation"
      />
      {/* Sheet – slides up from bottom using CSS dvh for iOS Safari stability */}
      <motion.div
        ref={sheetRef}
        initial={{ y: '100%' }}
        animate={controls}
        transition={{ type: 'spring', damping: 30, stiffness: 300, mass: 0.8 }}
        drag={isDragEnabled ? 'y' : false}
        dragControls={isDragEnabled ? dragControls : undefined}
        dragListener={false} // Disable default drag listener, we use dragControls
        dragConstraints={{ top: 0 }}
        dragElastic={{ top: 0, bottom: 0.5 }}
        onDragEnd={isDragEnabled ? handleDragEnd : undefined}
        className={cn(
          'fixed left-0 right-0 bottom-0 z-[60] flex flex-col bg-white shadow-[-10px_-8px_20px_-3px_rgba(0,0,0,0.1)] rounded-t-xl',
          'bottom-sheet-fallback', // CSS fallback class for browsers without dvh
          className
        )}
        style={{
          // Use dvh (dynamic viewport height) for iOS Safari - handles keyboard/banner smoothly
          // Fallback to vh for older browsers
          height: `${heightPercent}dvh`,
          // @ts-expect-error -- CSS fallback for browsers without dvh support
          '--fallback-height': `${heightPercent}vh`,
          touchAction: isDragEnabled ? 'none' : 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header – static, no drag */}
        {headerContent && (
          <div className="w-full shrink-0 pb-2 pt-2 px-4 bg-white rounded-t-xl">
            {headerContent}
          </div>
        )}
        {/* Content – no overflow so child list is the scroll container */}
        <div
          className={cn(
            !headerContent && 'rounded-t-xl',
            'flex-1 flex flex-col min-h-0 overflow-hidden'
          )}
        >
          {children}
        </div>
      </motion.div>
    </>
  );
};

export default BottomSheet;
